import React, { useState, useEffect, useRef } from 'react';
import { 
  FileImage, Wand2, Download, Save, Trash2, Edit2, Eye, Plus,
  Type, Image, Palette, Layout, RotateCcw, Copy, Sparkles
} from 'lucide-react';
import { Button } from '../ui/button';
import html2canvas from 'html2canvas';

const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

// Default flyer template matching the Okinawa Sign Studio style
const defaultFlyerData = {
  id: null,
  name: 'New Flyer',
  // Header section
  logoUrl: '',
  companyName: 'OKINAWA SIGN STUDIO',
  tagline: 'Bring Your Brand to Life',
  // Main content
  headline: 'Custom Signs & Designs',
  subheadline: 'Professional Quality for Your Business',
  // Services/Features (up to 6)
  features: [
    { icon: '✨', title: 'LED Signs', description: 'Bright & Energy Efficient' },
    { icon: '🎨', title: 'Custom Design', description: 'Unique to Your Brand' },
    { icon: '🏆', title: 'Premium Quality', description: 'Built to Last' },
    { icon: '🚀', title: 'Fast Delivery', description: '2-4 Week Turnaround' },
    { icon: '💰', title: 'Fair Pricing', description: 'Competitive Rates' },
    { icon: '🌍', title: 'Worldwide Ship', description: 'Global Delivery' }
  ],
  // Call to action
  ctaText: 'Get a Free Quote Today!',
  ctaSubtext: 'DM or Email Us',
  // Contact info
  phone: '+81 98-XXX-XXXX',
  email: 'info@okinawasignstudio.com',
  website: 'okinawasignstudio.com',
  // Colors (neon theme)
  primaryColor: '#00ffff',
  secondaryColor: '#ff00ff',
  accentColor: '#00ff88',
  backgroundColor: '#0a0a0a',
  textColor: '#ffffff'
};

const FlyerGenerator = ({ isDark, authFetch, showToast }) => {
  const [flyerData, setFlyerData] = useState(defaultFlyerData);
  const [savedFlyers, setSavedFlyers] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [activeSection, setActiveSection] = useState('content');
  const [aiPrompt, setAiPrompt] = useState('');
  const flyerRef = useRef(null);

  // Fetch saved flyers on mount
  useEffect(() => {
    fetchSavedFlyers();
    fetchCompanySettings();
  }, []);

  const fetchSavedFlyers = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/admin/flyers`);
      if (response.ok) {
        const data = await response.json();
        setSavedFlyers(data);
      }
    } catch (error) {
      console.error('Error fetching flyers:', error);
    }
  };

  const fetchCompanySettings = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/admin/company-settings`);
      if (response.ok) {
        const data = await response.json();
        setFlyerData(prev => ({
          ...prev,
          companyName: data.company_name || prev.companyName,
          phone: data.phone || prev.phone,
          email: data.email || prev.email,
          website: data.website || 'okinawasignstudio.com'
        }));
      }
    } catch (error) {
      console.error('Error fetching company settings:', error);
    }
  };

  // Generate flyer with AI
  const generateWithAI = async () => {
    if (!aiPrompt.trim()) {
      showToast('Please enter a description for your flyer', 'error');
      return;
    }
    
    setIsGenerating(true);
    try {
      const response = await authFetch(`${API_URL}/api/admin/generate-flyer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: aiPrompt, currentData: flyerData })
      });
      
      if (response.ok) {
        const data = await response.json();
        setFlyerData(prev => ({ ...prev, ...data }));
        showToast('Flyer generated successfully!');
      } else {
        showToast('Failed to generate flyer', 'error');
      }
    } catch (error) {
      console.error('Error generating flyer:', error);
      showToast('Error generating flyer', 'error');
    }
    setIsGenerating(false);
  };

  // Save flyer
  const saveFlyer = async () => {
    setIsSaving(true);
    try {
      const method = flyerData.id ? 'PUT' : 'POST';
      const url = flyerData.id 
        ? `${API_URL}/api/admin/flyers/${flyerData.id}`
        : `${API_URL}/api/admin/flyers`;
      
      const response = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(flyerData)
      });
      
      if (response.ok) {
        const data = await response.json();
        setFlyerData(prev => ({ ...prev, id: data.id }));
        fetchSavedFlyers();
        showToast('Flyer saved successfully!');
      } else {
        showToast('Failed to save flyer', 'error');
      }
    } catch (error) {
      console.error('Error saving flyer:', error);
      showToast('Error saving flyer', 'error');
    }
    setIsSaving(false);
  };

  // Download flyer as PNG
  const downloadAsPNG = async () => {
    if (!flyerRef.current) return;
    
    try {
      const canvas = await html2canvas(flyerRef.current, {
        scale: 2,
        backgroundColor: flyerData.backgroundColor,
        useCORS: true
      });
      
      const link = document.createElement('a');
      link.download = `${flyerData.name.replace(/\s+/g, '_')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      showToast('Flyer downloaded as PNG!');
    } catch (error) {
      console.error('Error downloading PNG:', error);
      showToast('Error downloading flyer', 'error');
    }
  };

  // Download as PDF (using print)
  const downloadAsPDF = () => {
    setShowPreview(true);
    setTimeout(() => {
      window.print();
    }, 500);
  };

  // Load saved flyer
  const loadFlyer = (flyer) => {
    setFlyerData(flyer);
    showToast('Flyer loaded for editing');
  };

  // Delete flyer
  const deleteFlyer = async (flyerId) => {
    if (!confirm('Are you sure you want to delete this flyer?')) return;
    
    try {
      const response = await authFetch(`${API_URL}/api/admin/flyers/${flyerId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        fetchSavedFlyers();
        if (flyerData.id === flyerId) {
          setFlyerData(defaultFlyerData);
        }
        showToast('Flyer deleted');
      }
    } catch (error) {
      console.error('Error deleting flyer:', error);
    }
  };

  // Reset to defaults
  const resetFlyer = () => {
    setFlyerData({ ...defaultFlyerData, id: null, name: 'New Flyer' });
  };

  // Update feature
  const updateFeature = (index, field, value) => {
    const newFeatures = [...flyerData.features];
    newFeatures[index] = { ...newFeatures[index], [field]: value };
    setFlyerData({ ...flyerData, features: newFeatures });
  };

  return (
    <div className="space-y-6" data-testid="flyer-generator-tab">
      {/* Header */}
      <div 
        className="p-6 rounded-lg"
        style={{
          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
          border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`
        }}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <FileImage className="w-6 h-6" style={{ color: isDark ? '#ff00ff' : '#d946ef' }} />
            <div>
              <h2 className="text-xl font-bold" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>
                Flyer Generator
              </h2>
              <p className="text-sm" style={{ color: isDark ? '#888' : '#64748b' }}>
                Create professional marketing flyers with AI assistance
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setShowPreview(!showPreview)}
              variant="outline"
              style={{ borderColor: isDark ? '#00ffff' : '#0ea5e9', color: isDark ? '#00ffff' : '#0ea5e9' }}
            >
              <Eye className="w-4 h-4 mr-1" /> {showPreview ? 'Hide' : 'Preview'}
            </Button>
            <Button
              onClick={resetFlyer}
              variant="outline"
              style={{ borderColor: isDark ? '#888' : '#94a3b8', color: isDark ? '#888' : '#64748b' }}
            >
              <RotateCcw className="w-4 h-4 mr-1" /> Reset
            </Button>
          </div>
        </div>

        {/* AI Generator */}
        <div 
          className="p-4 rounded-lg mb-6"
          style={{
            background: isDark 
              ? 'linear-gradient(135deg, rgba(255, 0, 255, 0.1) 0%, rgba(0, 255, 255, 0.1) 100%)'
              : 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(14, 165, 233, 0.1) 100%)',
            border: `1px solid ${isDark ? '#ff00ff40' : '#8b5cf640'}`
          }}
        >
          <div className="flex items-center gap-2 mb-3">
            <Wand2 className="w-5 h-5" style={{ color: isDark ? '#ff00ff' : '#8b5cf6' }} />
            <span className="font-semibold" style={{ color: isDark ? '#ff00ff' : '#8b5cf6' }}>
              AI Flyer Generator
            </span>
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="Describe your flyer (e.g., 'Grand opening sale for a coffee shop with 20% off')"
              className="flex-1 p-3 rounded-lg"
              style={{
                backgroundColor: isDark ? '#1a1a1a' : '#fff',
                border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`,
                color: isDark ? '#fff' : '#000'
              }}
            />
            <Button
              onClick={generateWithAI}
              disabled={isGenerating}
              style={{
                background: isDark 
                  ? 'linear-gradient(135deg, #ff00ff 0%, #00ffff 100%)'
                  : 'linear-gradient(135deg, #8b5cf6 0%, #0ea5e9 100%)',
                color: '#fff'
              }}
            >
              {isGenerating ? (
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-1" /> Generate
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Editor Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'content', label: 'Content', icon: Type },
            { id: 'features', label: 'Features', icon: Layout },
            { id: 'colors', label: 'Colors', icon: Palette },
            { id: 'contact', label: 'Contact', icon: FileImage }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveSection(tab.id)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-all"
              style={{
                backgroundColor: activeSection === tab.id 
                  ? (isDark ? '#ff00ff20' : '#8b5cf620')
                  : 'transparent',
                color: activeSection === tab.id 
                  ? (isDark ? '#ff00ff' : '#8b5cf6')
                  : (isDark ? '#888' : '#64748b'),
                border: `1px solid ${activeSection === tab.id ? (isDark ? '#ff00ff' : '#8b5cf6') : 'transparent'}`
              }}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content Section */}
        {activeSection === 'content' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>
                Flyer Name
              </label>
              <input
                type="text"
                value={flyerData.name}
                onChange={(e) => setFlyerData({ ...flyerData, name: e.target.value })}
                className="w-full p-3 rounded-lg"
                style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>
                Company Name
              </label>
              <input
                type="text"
                value={flyerData.companyName}
                onChange={(e) => setFlyerData({ ...flyerData, companyName: e.target.value })}
                className="w-full p-3 rounded-lg"
                style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>
                Tagline
              </label>
              <input
                type="text"
                value={flyerData.tagline}
                onChange={(e) => setFlyerData({ ...flyerData, tagline: e.target.value })}
                className="w-full p-3 rounded-lg"
                style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>
                Headline
              </label>
              <input
                type="text"
                value={flyerData.headline}
                onChange={(e) => setFlyerData({ ...flyerData, headline: e.target.value })}
                className="w-full p-3 rounded-lg"
                style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>
                Subheadline
              </label>
              <input
                type="text"
                value={flyerData.subheadline}
                onChange={(e) => setFlyerData({ ...flyerData, subheadline: e.target.value })}
                className="w-full p-3 rounded-lg"
                style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>
                Call to Action
              </label>
              <input
                type="text"
                value={flyerData.ctaText}
                onChange={(e) => setFlyerData({ ...flyerData, ctaText: e.target.value })}
                className="w-full p-3 rounded-lg"
                style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>
                CTA Subtext
              </label>
              <input
                type="text"
                value={flyerData.ctaSubtext}
                onChange={(e) => setFlyerData({ ...flyerData, ctaSubtext: e.target.value })}
                className="w-full p-3 rounded-lg"
                style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
              />
            </div>
          </div>
        )}

        {/* Features Section */}
        {activeSection === 'features' && (
          <div className="space-y-4">
            <p className="text-sm" style={{ color: isDark ? '#888' : '#64748b' }}>
              Edit the 6 feature boxes that appear on your flyer
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {flyerData.features.map((feature, index) => (
                <div 
                  key={index}
                  className="p-4 rounded-lg"
                  style={{
                    backgroundColor: isDark ? '#1a1a1a' : '#fff',
                    border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`
                  }}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <input
                      type="text"
                      value={feature.icon}
                      onChange={(e) => updateFeature(index, 'icon', e.target.value)}
                      className="w-12 text-center text-xl p-1 rounded"
                      style={{ backgroundColor: isDark ? '#0a0a0a' : '#f8fafc', border: `1px solid ${isDark ? '#444' : '#e2e8f0'}` }}
                      placeholder="🔥"
                    />
                    <span className="text-xs" style={{ color: isDark ? '#666' : '#94a3b8' }}>Icon/Emoji</span>
                  </div>
                  <input
                    type="text"
                    value={feature.title}
                    onChange={(e) => updateFeature(index, 'title', e.target.value)}
                    placeholder="Title"
                    className="w-full p-2 rounded mb-2"
                    style={{ backgroundColor: isDark ? '#0a0a0a' : '#f8fafc', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#444' : '#e2e8f0'}` }}
                  />
                  <input
                    type="text"
                    value={feature.description}
                    onChange={(e) => updateFeature(index, 'description', e.target.value)}
                    placeholder="Description"
                    className="w-full p-2 rounded"
                    style={{ backgroundColor: isDark ? '#0a0a0a' : '#f8fafc', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#444' : '#e2e8f0'}` }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Colors Section */}
        {activeSection === 'colors' && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { key: 'primaryColor', label: 'Primary (Cyan)', default: '#00ffff' },
              { key: 'secondaryColor', label: 'Secondary (Pink)', default: '#ff00ff' },
              { key: 'accentColor', label: 'Accent (Green)', default: '#00ff88' },
              { key: 'backgroundColor', label: 'Background', default: '#0a0a0a' },
              { key: 'textColor', label: 'Text', default: '#ffffff' }
            ].map(color => (
              <div key={color.key}>
                <label className="block text-sm font-medium mb-2" style={{ color: isDark ? '#ccc' : '#475569' }}>
                  {color.label}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={flyerData[color.key]}
                    onChange={(e) => setFlyerData({ ...flyerData, [color.key]: e.target.value })}
                    className="w-12 h-12 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={flyerData[color.key]}
                    onChange={(e) => setFlyerData({ ...flyerData, [color.key]: e.target.value })}
                    className="flex-1 p-2 rounded text-sm"
                    style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Contact Section */}
        {activeSection === 'contact' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>
                Phone
              </label>
              <input
                type="text"
                value={flyerData.phone}
                onChange={(e) => setFlyerData({ ...flyerData, phone: e.target.value })}
                className="w-full p-3 rounded-lg"
                style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>
                Email
              </label>
              <input
                type="text"
                value={flyerData.email}
                onChange={(e) => setFlyerData({ ...flyerData, email: e.target.value })}
                className="w-full p-3 rounded-lg"
                style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>
                Website
              </label>
              <input
                type="text"
                value={flyerData.website}
                onChange={(e) => setFlyerData({ ...flyerData, website: e.target.value })}
                className="w-full p-3 rounded-lg"
                style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
              />
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 mt-6 pt-6 border-t" style={{ borderColor: isDark ? '#333' : '#e2e8f0' }}>
          <Button
            onClick={saveFlyer}
            disabled={isSaving}
            className="flex items-center gap-2"
            style={{ background: isDark ? 'linear-gradient(135deg, #00ff88 0%, #00ffff 100%)' : 'linear-gradient(135deg, #10b981 0%, #0ea5e9 100%)', color: isDark ? '#000' : '#fff' }}
          >
            <Save className="w-4 h-4" />
            {isSaving ? 'Saving...' : 'Save Flyer'}
          </Button>
          <Button
            onClick={downloadAsPNG}
            variant="outline"
            className="flex items-center gap-2"
            style={{ borderColor: isDark ? '#ff00ff' : '#d946ef', color: isDark ? '#ff00ff' : '#d946ef' }}
          >
            <Download className="w-4 h-4" />
            Download PNG
          </Button>
          <Button
            onClick={downloadAsPDF}
            variant="outline"
            className="flex items-center gap-2"
            style={{ borderColor: isDark ? '#ffff00' : '#eab308', color: isDark ? '#ffff00' : '#eab308' }}
          >
            <Download className="w-4 h-4" />
            Download PDF
          </Button>
        </div>
      </div>

      {/* Live Preview */}
      {showPreview && (
        <div 
          className="p-6 rounded-lg"
          style={{
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
            border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`
          }}
        >
          <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: isDark ? '#00ffff' : '#0ea5e9' }}>
            <Eye className="w-5 h-5" /> Live Preview
          </h3>
          
          {/* Flyer Preview */}
          <div className="flex justify-center">
            <div 
              ref={flyerRef}
              className="w-full max-w-md p-6 rounded-xl"
              style={{
                backgroundColor: flyerData.backgroundColor,
                color: flyerData.textColor,
                boxShadow: `0 0 60px ${flyerData.primaryColor}40`
              }}
            >
              {/* Header */}
              <div className="text-center mb-6">
                <h1 
                  className="text-2xl font-bold mb-1"
                  style={{ 
                    color: flyerData.primaryColor,
                    textShadow: `0 0 20px ${flyerData.primaryColor}`
                  }}
                >
                  {flyerData.companyName}
                </h1>
                <p 
                  className="text-sm"
                  style={{ 
                    background: `linear-gradient(90deg, ${flyerData.primaryColor}, ${flyerData.secondaryColor})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  {flyerData.tagline}
                </p>
              </div>

              {/* Main Content */}
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold mb-2" style={{ color: flyerData.textColor }}>
                  {flyerData.headline}
                </h2>
                <p className="text-sm opacity-80">{flyerData.subheadline}</p>
              </div>

              {/* Features Grid */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                {flyerData.features.map((feature, index) => (
                  <div 
                    key={index}
                    className="p-3 rounded-lg text-center"
                    style={{
                      backgroundColor: `${flyerData.primaryColor}15`,
                      border: `1px solid ${flyerData.primaryColor}40`
                    }}
                  >
                    <span className="text-2xl">{feature.icon}</span>
                    <p className="font-semibold text-sm mt-1" style={{ color: flyerData.primaryColor }}>
                      {feature.title}
                    </p>
                    <p className="text-xs opacity-70">{feature.description}</p>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div 
                className="p-4 rounded-lg text-center mb-4"
                style={{
                  background: `linear-gradient(135deg, ${flyerData.primaryColor}30, ${flyerData.secondaryColor}30)`,
                  border: `2px solid ${flyerData.secondaryColor}`
                }}
              >
                <p className="font-bold text-lg" style={{ color: flyerData.secondaryColor }}>
                  {flyerData.ctaText}
                </p>
                <p className="text-sm opacity-80">{flyerData.ctaSubtext}</p>
              </div>

              {/* Contact */}
              <div className="text-center text-sm opacity-80">
                <p>{flyerData.phone} | {flyerData.email}</p>
                <p style={{ color: flyerData.accentColor }}>{flyerData.website}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Saved Flyers */}
      {savedFlyers.length > 0 && (
        <div 
          className="p-6 rounded-lg"
          style={{
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
            border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`
          }}
        >
          <h3 className="font-semibold mb-4" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>
            Saved Flyers ({savedFlyers.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {savedFlyers.map((flyer) => (
              <div 
                key={flyer.id}
                className="p-4 rounded-lg"
                style={{
                  backgroundColor: isDark ? '#1a1a1a' : '#fff',
                  border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold" style={{ color: isDark ? '#fff' : '#0f172a' }}>
                    {flyer.name}
                  </h4>
                  <div className="flex gap-1">
                    <button
                      onClick={() => loadFlyer(flyer)}
                      className="p-1.5 rounded hover:bg-cyan-500/20"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4" style={{ color: isDark ? '#00ffff' : '#0ea5e9' }} />
                    </button>
                    <button
                      onClick={() => deleteFlyer(flyer.id)}
                      className="p-1.5 rounded hover:bg-red-500/20"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </div>
                <p className="text-xs" style={{ color: isDark ? '#666' : '#94a3b8' }}>
                  {flyer.headline || flyer.companyName}
                </p>
                <p className="text-xs mt-1" style={{ color: isDark ? '#444' : '#cbd5e1' }}>
                  {new Date(flyer.created_at).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FlyerGenerator;
